/**
 * VakzarasPage — Daily Close Wizard (Napi Zaras)
 * Story 62-12: Cash Management Frontend UI
 * P-CSH-1: Vakzaras -> nyilt zaras (ERP-TESZT 24)
 *
 * Nyilt zaras: boltvezeto+ LATJA az elvart osszegeket.
 * +-2.000 Ft tolerancia: SZH zsebbol kiegyenlitve, jegyzokonyv NEM kell.
 */

import { cashActionErrorToastKey } from '@/api/cash-management';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useBlindClose, useCloseSummary, useTodayRegister } from '@/hooks/useCashManagement';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type WizardStep = 'COUNTING' | 'REVIEW' | 'DONE';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function VakzarasPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const POCKET_LABELS: Record<string, string> = {
    REGISTER: t('finance.cashRegister'),
    DEPOSIT: t('finance.deposit'),
    CREDIT: t('finance.szhLabel'),
  };
  const navigate = useNavigate();
  const { register, isLoading } = useTodayRegister();
  const blindClose = useBlindClose(register?.id ?? '');
  const { closeSummary } = useCloseSummary(register?.id);

  const [step, setStep] = useState<WizardStep>('COUNTING');
  const [counts, setCounts] = useState({
    REGISTER: '',
    DEPOSIT: '',
    CREDIT: '',
  });
  const [result, setResult] = useState<{
    discrepancy: number;
    withinTolerance: boolean;
    status: string;
    requiresRecount?: boolean;
    pocketDetails?: Array<{
      type: string;
      expected: number;
      physical: number;
      discrepancy: number;
    }>;
    autoAccepted?: boolean;
  } | null>(null);

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">{t('finance.loadingData')}</div>;
  }

  if (!register) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">{t('finance.noRegisterToClose')}</p>
            <Button onClick={() => navigate('/finance/cash')}>{t('common.back')}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleStartCounting = async () => {
    try {
      await blindClose.start.mutateAsync();
    } catch (err) {
      toast({ title: t(cashActionErrorToastKey(err)), variant: 'destructive' });
      return;
    }
    setStep('COUNTING');
  };

  const handleSubmitCounts = async () => {
    const pocketCounts = (['REGISTER', 'DEPOSIT', 'CREDIT'] as const).map(type => ({
      pocketType: type,
      physicalAmount: parseFloat(counts[type]) || 0,
    }));

    let res;
    try {
      res = await blindClose.submit.mutateAsync(pocketCounts);
    } catch (err) {
      toast({ title: t(cashActionErrorToastKey(err)), variant: 'destructive' });
      return;
    }
    const data = res.data;

    setResult({
      discrepancy: data.discrepancy,
      withinTolerance: data.withinTolerance,
      status: data.newStatus,
      requiresRecount: data.requiresRecount,
      pocketDetails: data.pocketDetails,
      autoAccepted: data.autoAccepted,
    });
    setStep('REVIEW');
  };

  const handleFinalize = async () => {
    const pocketCounts = (['REGISTER', 'DEPOSIT', 'CREDIT'] as const).map(type => ({
      pocketType: type,
      physicalAmount: parseFloat(counts[type]) || 0,
    }));

    try {
      await blindClose.finalize.mutateAsync(pocketCounts);
    } catch (err) {
      toast({ title: t(cashActionErrorToastKey(err)), variant: 'destructive' });
      return;
    }
    setStep('DONE');
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('finance.dailyClose')}</h1>
        <Button variant="outline" onClick={() => navigate('/finance/cash')}>
          {t('common.back')}
        </Button>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2 mb-6">
        {(['COUNTING', 'REVIEW', 'DONE'] as const).map((s, i) => (
          <div
            key={s}
            className={`flex-1 h-2 rounded-full ${
              step === s
                ? 'bg-primary'
                : ['COUNTING', 'REVIEW', 'DONE'].indexOf(step) > i
                  ? 'bg-green-500'
                  : 'bg-gray-200 dark:bg-gray-700'
            }`}
          />
        ))}
      </div>

      {step === 'COUNTING' && (
        <Card>
          <CardHeader>
            <CardTitle>{t('finance.physicalCount')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('finance.physicalCountDesc')}</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {(['REGISTER', 'DEPOSIT', 'CREDIT'] as const).map(type => {
              const expectedPocket = closeSummary?.pockets.find(p => p.type === type);
              return (
                <div key={type} className="flex items-center gap-4">
                  <label className="w-24 font-medium">{POCKET_LABELS[type]}</label>
                  <Input
                    type="number"
                    placeholder={t('finance.physicalAmountPlaceholder')}
                    value={counts[type]}
                    onChange={e => setCounts({ ...counts, [type]: e.target.value })}
                    className="flex-1"
                  />
                  {/* P-CSH-1: Show expected balance (nyilt zaras) */}
                  {expectedPocket !== undefined && (
                    <span className="text-sm text-muted-foreground w-40 text-right">
                      {t('finance.expectedBalance')}:{' '}
                      {formatCurrency(expectedPocket.expectedBalance)}
                    </span>
                  )}
                </div>
              );
            })}

            {/* P-CSH-1: Show tolerance info */}
            {closeSummary && (
              <div className="text-sm text-muted-foreground pt-2">{t('finance.toleranceInfo')}</div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleStartCounting}
                variant="outline"
                disabled={blindClose.start.isPending}
              >
                {t('finance.startDailyClose')}
              </Button>
              <Button onClick={handleSubmitCounts} disabled={blindClose.submit.isPending}>
                {t('finance.compare')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'REVIEW' && result && (
        <Card>
          <CardHeader>
            <CardTitle>{t('finance.result')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`p-4 rounded-lg ${
                result.withinTolerance
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-500'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-500'
              }`}
            >
              <div className="text-lg font-bold">
                {result.withinTolerance ? t('finance.match') : t('finance.discrepancy')}
              </div>
              <div className="text-2xl font-bold mt-1">{formatCurrency(result.discrepancy)}</div>
              <div className="text-sm text-muted-foreground mt-1">{t('finance.tolerance')}</div>
            </div>

            {/* P-CSH-1: Per-pocket breakdown */}
            {result.pocketDetails && result.pocketDetails.length > 0 && (
              <div className="space-y-2">
                {result.pocketDetails.map(pd => (
                  <div
                    key={pd.type}
                    className="flex justify-between items-center text-sm border-b pb-2"
                  >
                    <span className="font-medium">{POCKET_LABELS[pd.type] ?? pd.type}</span>
                    <div className="flex gap-4">
                      <span className="text-muted-foreground">
                        {t('finance.expectedBalance')}: {formatCurrency(pd.expected)}
                      </span>
                      <span className={pd.discrepancy === 0 ? 'text-green-600' : 'text-red-600'}>
                        {pd.discrepancy > 0 ? '+' : ''}
                        {formatCurrency(pd.discrepancy)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* P-CSH-1: Auto-accepted from SZH pocket */}
            {result.autoAccepted && result.withinTolerance && (
              <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-400 text-green-700 dark:text-green-300 text-sm">
                {t('finance.autoAccepted')}
              </div>
            )}

            {/* Over-tolerance warning */}
            {!result.withinTolerance && !result.requiresRecount && (
              <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-500">
                <p className="font-medium text-red-700 dark:text-red-300">
                  {t('finance.overToleranceWarning')}
                </p>
              </div>
            )}

            {result.requiresRecount && (
              <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-500">
                <p className="font-medium">{t('finance.recountRequired')}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('finance.recountRequiredDesc')}
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              {result.requiresRecount && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep('COUNTING');
                    setResult(null);
                  }}
                >
                  {t('finance.recount')}
                </Button>
              )}
              <Button onClick={handleFinalize} disabled={blindClose.finalize.isPending}>
                {result.withinTolerance
                  ? t('finance.finalizeClose')
                  : t('finance.discrepancyReport')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 'DONE' && (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-green-500 text-5xl mb-4">&#10003;</div>
            <h2 className="text-xl font-bold mb-2">{t('finance.dailyCloseComplete')}</h2>
            <p className="text-muted-foreground mb-4">{t('finance.registerClosedSuccess')}</p>
            <Button onClick={() => navigate('/finance/cash')}>{t('finance.backToRegister')}</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
